// ============================================================================
// PARAMÈTRES → ARCHIVAGE DES ARTICLES — sortir les vieux modèles des recherches sans toucher à l'histoire des ventes.
//
// ⚠️ ARCHIVÉ ≠ RETIRÉ DE LA VENTE (décision utilisateur 2026-10-03) : un article archivé N'EXISTE PLUS SUR AUCUN
//    APPAREIL — la synchronisation l'efface des caisses et de Mobile Partner, son stock et ses photos avec — et sort de
//    toutes les recherches du back-office (2026-10-04). Il ne se retrouve QUE dans la liste de cet onglet, d'où il se
//    remet en service. L'écran le dit, sans quoi on croirait le retrouver par son code dans les Articles.
// ⚠️ L'APERÇU EST UNE MESURE DU SERVEUR, jamais un calcul ici : il redésigne avec les MÊMES critères que l'écriture, et
//    l'écriture rend ce qu'elle a réellement fait (le catalogue a pu bouger entre les deux).
// ⚠️ AUCUNE SAISON COCHÉE = TOUTES (décision utilisateur) : l'aperçu et la confirmation l'écrivent en toutes lettres —
//    un choix vide ne doit jamais se lire « aucune ».
// ============================================================================
import { useCallback, useEffect, useRef, useState } from 'react';
import { alert, Button, confirm, EmptyState, KpiRow, Picker, showToast, Switch, Trans } from '@orgazone/ui-web';
import { date as formatDate } from '@orgazone/date-format';
import { iconPath } from '@orgazone/ui-tokens';
import {
  api, ARCHIVE_UNSOLD_MONTHS,
  type ArchiveCriteria, type ArchivedPage, type ArchiveGuards, type ArchivePreview, type ArchiveUnsoldMonths, type ArticleFacets,
} from '../api';
import { key, nombre, t, type Translatable } from '../i18n';
import { fieldLabel } from '../vocabulary';

const DEFAULT_CRITERIA: ArchiveCriteria = {
  unsoldMonths: 24, seasons: [], brand: null,
  guards: { stockZero: true, noOpenOrder: true, noPromotion: true },
};

/** Le libellé d'une durée — « 3 ans » et « 5 ans » au-delà de deux ans, comme le dessin. */
const MONTH_LABELS: Record<ArchiveUnsoldMonths, Translatable> = {
  12: key('12 mois'), 18: key('18 mois'), 24: key('24 mois'), 36: key('3 ans'), 60: key('5 ans'),
};

/** Les garde-fous, dans l'ordre du dessin. ⚠️ « Stock à zéro » porte sur CE magasin (décision utilisateur). */
const GUARDS: { k: keyof ArchiveGuards; label: Translatable; hint: Translatable }[] = [
  { k: 'stockZero', label: key('Stock à zéro dans ce magasin'),
    hint: key('Aucune pièce en stock ici, ni en négatif — on n’archive jamais une pièce encore en rayon. Les autres magasins ne sont pas consultés.') },
  { k: 'noOpenOrder', label: key('Aucune commande en cours'),
    hint: key('Ni commande fournisseur qui l’attend encore, ni bon de livraison en attente d’entrée.') },
  { k: 'noPromotion', label: key('Hors promotion et hors collection active'),
    hint: key('Un article encore mis en avant reste au catalogue.') },
];

/** Les tracés des trois repères, relevés de la maquette. */
const ICON_CATALOGUE = iconPath('lines');
const ICON_VARIANTS = 'M3 3h7v7H3zM14 3h7v7h-7zM3 14h7v7H3zM14 14h7v7h-7z';
// ⚠️ L'ARCHIVE EST UNE ICÔNE DE LA CHARTE : son tracé se LIT au registre, jamais recopié — une copie ne suivrait pas un
//    changement de dessin.
const ICON_ARCHIVED = iconPath('lidBox');

/**
 * CE QUE LES SAISONS CHOISIES COUVRENT, en toutes lettres. ⚠️ UN CHOIX VIDE VAUT TOUTES (décision utilisateur), et la phrase
 * le DIT : l'aperçu et la confirmation la lisent ici, sans quoi l'un des deux finirait par laisser lire « aucune ».
 */
export function seasonsScope(seasons: readonly string[]): string {
  return seasons.length === 0
    ? t('Toutes les saisons sont concernées — aucune n’est cochée.')
    : t('Saisons concernées : {s}.', { s: seasons.join(', ') });
}

/** Une date fiscale `AAAAMMJJHHMMSS` (heure du magasin) rendue comme un jour. */
function fiscalDay(v: string | null): string {
  if (!v || !/^\d{8}/.test(v)) return '—';
  return formatDate(new Date(Number(v.slice(0, 4)), Number(v.slice(4, 6)) - 1, Number(v.slice(6, 8))));
}

/** Le bloc de l'onglet « Archivage des articles » : les critères, l'aperçu, le geste, puis la liste des archivés. */
export function ArticleArchive() {
  const [criteria, setCriteria] = useState<ArchiveCriteria>(DEFAULT_CRITERIA);
  const [preview, setPreview] = useState<ArchivePreview | null>(null);
  const [previewErr, setPreviewErr] = useState<string | null>(null);
  const [facets, setFacets] = useState<ArticleFacets | null>(null);
  const [busy, setBusy] = useState(false);
  const [listOpen, setListOpen] = useState(false);
  // ⚠️ SEULE LA DERNIÈRE MESURE S'AFFICHE : deux critères changés vite font partir deux lectures, et la plus lente
  //    pourrait arriver la dernière — l'écran montrerait alors l'aperçu de critères qui ne sont plus à l'écran.
  const lastAsked = useRef(0);

  const measure = useCallback((c: ArchiveCriteria) => {
    const asked = ++lastAsked.current;
    setPreviewErr(null);
    api.archivePreview(c)
      .then((p) => { if (asked === lastAsked.current) setPreview(p); })
      .catch((e: unknown) => { if (asked === lastAsked.current) { setPreview(null); setPreviewErr(e instanceof Error ? e.message : String(e)); } });
  }, []);
  useEffect(() => { measure(criteria); }, [criteria, measure]);
  useEffect(() => {
    // ⚠️ Best-effort ASSUMÉ : sans les valeurs du catalogue, les sélecteurs restent sur « toutes » et le DISENT ; les
    //    critères qui restent (durée, garde-fous) fonctionnent sans elles.
    api.articleFacets().then(setFacets).catch(() => setFacets(null));
  }, []);

  const change = (patch: Partial<ArchiveCriteria>) => setCriteria((c) => ({ ...c, ...patch }));
  const tooMany = preview !== null && preview.variants > preview.maxVariants;

  const run = async () => {
    if (!preview || preview.models === 0) return;
    const scope = seasonsScope(criteria.seasons);
    const ok = await confirm({
      title: t('Archiver {n} modèle ?', { n: preview.models }),
      message: `${scope} ${t('Ils disparaissent des caisses, de Mobile Partner et des recherches du back-office ; leurs ventes passées restent lisibles et aucune ligne n’est supprimée. Ils se remettent en service depuis la liste des archivés.')}`,
      confirmLabel: t('Archiver'), tone: 'info',
    });
    if (!ok) return;
    setBusy(true);
    try {
      const r = await api.archiveArticles(criteria, `arch-${crypto.randomUUID()}`);
      showToast(t('{n} modèle archivé', { n: r.models }));
      measure(criteria);
    } catch (e) {
      await alert({ title: t('Archivage refusé'), message: e instanceof Error ? e.message : String(e) });
    } finally { setBusy(false); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', gap: 9, alignItems: 'flex-start', padding: '13px 15px', borderRadius: 'var(--r-md)', background: 'var(--teal-100)', border: '1px solid var(--teal-400)', color: 'var(--teal-800)', fontSize: 'var(--t-dense)', lineHeight: 1.6 }}>
        <span>
          <Trans
            k="Archiver <1>sort l’article des recherches, pas de l’histoire</1> : une vente de 2019 continue de nommer son article, son prix et sa taille. La ligne n’est jamais supprimée — elle est marquée archivée, et ni les caisses ni Mobile Partner ne la portent plus."
            parts={[(x) => <strong>{x}</strong>]}
          />
        </span>
      </div>

      <KpiRow style={{ marginBottom: 0 }} items={[
        { label: t('Catalogue actif'), value: preview ? nombre(preview.activeModels) : null, sub: t('modèles en service'), icon: ICON_CATALOGUE, tone: 'teal' },
        { label: t('Déclinaisons'), value: preview ? nombre(preview.activeVariants) : null, sub: t('la table qui pèse sur les recherches'), icon: ICON_VARIANTS, tone: 'violet' },
        { label: t('Déjà archivés'), value: preview ? nombre(preview.archivedModels) : null, sub: t('absents des caisses, intacts au journal'), icon: ICON_ARCHIVED, tone: 'ink' },
      ]} />

      <div style={{ border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', background: 'var(--paper)', padding: '16px 18px' }}>
        <div style={{ fontSize: 'var(--t-h4)', fontWeight: 700, color: 'var(--ink-900)' }}>{t('Ce qui part aux archives')}</div>
        <div style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-500)', marginTop: 3, marginBottom: 14, lineHeight: 1.5 }}>
          {t('Toutes les conditions doivent être réunies. Rien ne bouge avant votre confirmation.')}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 12 }}>
          <label style={{ display: 'block' }}>
            <span style={{ display: 'block', fontSize: 'var(--t-meta)', fontWeight: 600, color: 'var(--ink-500)', marginBottom: 5 }}>{t('Aucune vente depuis')}</span>
            <select
              id="bo-arch-months" value={criteria.unsoldMonths}
              onChange={(e) => change({ unsoldMonths: Number(e.target.value) as ArchiveUnsoldMonths })}
              style={{ width: '100%', height: 38, padding: '0 10px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', fontSize: 'var(--t-body)', color: 'var(--ink-900)' }}
            >
              {ARCHIVE_UNSOLD_MONTHS.map((m) => <option key={m} value={m}>{t(MONTH_LABELS[m])}</option>)}
            </select>
          </label>
          <div>
            <span style={{ display: 'block', fontSize: 'var(--t-meta)', fontWeight: 600, color: 'var(--ink-500)', marginBottom: 5 }}>{fieldLabel('season')}</span>
            <Picker
              id="bo-arch-seasons" variant="field" multiple label={fieldLabel('season')}
              allLabel={t('Toutes les saisons')} clearLabel={t('Toutes les saisons')}
              options={(facets?.values.sais ?? []).map((v) => ({ value: v.value, label: v.value }))}
              value={criteria.seasons} onChange={(v) => change({ seasons: v })}
              onSearch={(q) => api.articleFacetSearch('sais', q).then((r) => r.map((v) => ({ value: v.value, label: v.value })))}
            />
          </div>
          <div>
            <span style={{ display: 'block', fontSize: 'var(--t-meta)', fontWeight: 600, color: 'var(--ink-500)', marginBottom: 5 }}>{fieldLabel('brand')}</span>
            <Picker
              id="bo-arch-brand" variant="field" label={fieldLabel('brand')}
              allLabel={t('Toutes les marques')} clearLabel={t('Toutes les marques')}
              options={(facets?.values.marq ?? []).map((v) => ({ value: v.value, label: v.value }))}
              value={criteria.brand ?? ''} onChange={(v) => change({ brand: v || null })}
              onSearch={(q) => api.articleFacetSearch('marq', q).then((r) => r.map((v) => ({ value: v.value, label: v.value })))}
            />
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', marginTop: 12 }}>
          {GUARDS.map((g) => (
            <div key={g.k} style={{ padding: '9px 2px', borderTop: '1px solid var(--ink-100)' }}>
              <Switch
                checked={criteria.guards[g.k]}
                onChange={(on) => change({ guards: { ...criteria.guards, [g.k]: on } })}
                label={t(g.label)} hint={t(g.hint)}
              />
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16, padding: '13px 15px', borderRadius: 'var(--r-sm)', background: 'var(--ink-50)', flexWrap: 'wrap' }}>
          <span style={{ flex: 1, minWidth: 200, fontSize: 'var(--t-dense)', color: 'var(--ink-600)', lineHeight: 1.55 }}>
            {previewErr !== null
              ? <span style={{ color: 'var(--rose-fg)' }}>{t('L’aperçu n’a pas pu être mesuré : {e}', { e: previewErr })}</span>
              : preview === null ? t('Mesure en cours…')
                : <>
                  <strong style={{ color: 'var(--ink-800)' }}>{seasonsScope(criteria.seasons)}</strong>{' '}
                  {t('{n} modèle et {v} quitteraient les recherches. Le catalogue actif passerait à {r}.', {
                    n: preview.models, v: t('{n} déclinaison', { n: preview.variants }), r: t('{n} modèle', { n: preview.activeModels - preview.models }),
                  })}
                  {tooMany && <span style={{ display: 'block', color: 'var(--rose-fg)', marginTop: 4 }}>
                    {t('C’est au-delà de {declinaisons} par opération : choisissez des saisons ou une marque, et procédez en plusieurs fois.', { declinaisons: t('{n} déclinaison', { n: preview.maxVariants }) })}
                  </span>}
                </>}
          </span>
          <Button variant="solid" onClick={() => void run()} disabled={busy || !preview || preview.models === 0 || tooMany}>
            {t('Archiver ces articles')}
          </Button>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '13px 16px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', background: 'var(--paper)', flexWrap: 'wrap' }}>
        <span style={{ flex: 1, minWidth: 200, fontSize: 'var(--t-dense)', color: 'var(--ink-600)', lineHeight: 1.55 }}>
          {t('Un article archivé ne se retrouve qu’ici — ni dans les Articles, ni en caisse. Le remettre en service le fait redescendre avec son stock et ses photos.')}
        </span>
        <Button onClick={() => setListOpen((o) => !o)}>
          {listOpen ? t('Masquer les archivés') : t('Voir les archivés ({n})', { n: preview?.archivedModels ?? 0 })}
        </Button>
      </div>
      {listOpen && <ArchivedList onChanged={() => measure(criteria)} />}
    </div>
  );
}

/** La liste des modèles archivés — cherchable, paginée au serveur, et le geste qui les remet en service. */
function ArchivedList({ onChanged }: { onChanged: () => void }) {
  const [q, setQ] = useState('');
  const [page, setPage] = useState(0);
  const [data, setData] = useState<ArchivedPage | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [picked, setPicked] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const lastAsked = useRef(0);

  const load = useCallback((term: string, p: number) => {
    const asked = ++lastAsked.current;
    setErr(null);
    api.archivedArticles(term, p)
      .then((r) => { if (asked === lastAsked.current) setData(r); })
      .catch((e: unknown) => { if (asked === lastAsked.current) setErr(e instanceof Error ? e.message : String(e)); });
  }, []);
  useEffect(() => { load(q, page); }, [q, page, load]);

  const restore = async (keys: string[]) => {
    const ok = await confirm({
      title: t('Remettre {n} modèle en service ?', { n: keys.length }),
      message: t('Ils redescendent sur les caisses et Mobile Partner, avec leur stock et leurs photos, et reviennent dans les recherches.'),
      confirmLabel: t('Remettre en service'), tone: 'info',
    });
    if (!ok) return;
    setBusy(true);
    try {
      const r = await api.unarchiveArticles(keys, `unarch-${crypto.randomUUID()}`);
      showToast(t('{n} modèle remis en service', { n: r.models }));
      setPicked([]);
      load(q, page);
      onChanged();
    } catch (e) {
      await alert({ title: t('Remise en service refusée'), message: e instanceof Error ? e.message : String(e) });
    } finally { setBusy(false); }
  };

  const toggle = (k: string) => setPicked((p) => (p.includes(k) ? p.filter((x) => x !== k) : [...p, k]));
  const pages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  return (
    <div style={{ border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', background: 'var(--paper)', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          id="bo-arch-search" value={q} onChange={(e) => { setQ(e.target.value); setPage(0); }}
          placeholder={t('Nom, code modèle ou code-barres…')}
          style={{ flex: 1, minWidth: 200, height: 36, padding: '0 10px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', fontSize: 'var(--t-body)' }}
        />
        <Button variant="solid" disabled={busy || picked.length === 0} onClick={() => void restore(picked)}>
          {t('Remettre en service ({n})', { n: picked.length })}
        </Button>
      </div>
      {err !== null && <div style={{ color: 'var(--rose-fg)', fontSize: 'var(--t-dense)' }}>{t('La liste n’a pas pu être lue : {e}', { e: err })}</div>}
      {data !== null && data.rows.length === 0 && (
        <EmptyState compact icon="box" title={q ? t('Aucun archivé ne correspond') : t('Aucun article archivé')}
          note={q ? t('Essayez un autre nom ou un autre code.') : t('Les modèles archivés depuis cet onglet apparaîtront ici.')} />
      )}
      {data !== null && data.rows.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--t-dense)' }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--ink-500)' }}>
                <th style={{ padding: '6px 8px', width: 28 }} />
                <th style={{ padding: '6px 8px' }}>{t('Modèle')}</th>
                <th style={{ padding: '6px 8px', textAlign: 'right' }}>{t('Déclinaisons')}</th>
                <th style={{ padding: '6px 8px' }}>{t('Archivé le')}</th>
                <th style={{ padding: '6px 8px' }}>{t('Dernière vente')}</th>
                <th style={{ padding: '6px 8px' }} />
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.modelKey} style={{ borderTop: '1px solid var(--ink-100)' }}>
                  <td style={{ padding: '7px 8px' }}>
                    <input type="checkbox" aria-label={t('Choisir {m}', { m: r.name })} checked={picked.includes(r.modelKey)} onChange={() => toggle(r.modelKey)} />
                  </td>
                  <td style={{ padding: '7px 8px' }}>
                    <span style={{ display: 'block', fontWeight: 600, color: 'var(--ink-900)' }}>{r.name}</span>
                    <span style={{ display: 'block', color: 'var(--ink-500)' }}>{[r.articleCode, r.season, r.brand].filter(Boolean).join(' · ')}</span>
                  </td>
                  <td style={{ padding: '7px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{nombre(r.variants)}</td>
                  <td style={{ padding: '7px 8px' }}>{formatDate(r.archivedAt)}</td>
                  <td style={{ padding: '7px 8px' }}>{r.lastSale ? fiscalDay(r.lastSale) : t('jamais vendu')}</td>
                  <td style={{ padding: '7px 8px', textAlign: 'right' }}>
                    <Button size="sm" disabled={busy} onClick={() => void restore([r.modelKey])}>{t('Remettre en service')}</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data !== null && pages > 1 && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'flex-end', fontSize: 'var(--t-dense)', color: 'var(--ink-600)' }}>
          <Button size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>{t('Précédente')}</Button>
          <span>{t('Page {p} sur {total}', { p: page + 1, total: pages })}</span>
          <Button size="sm" disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)}>{t('Suivante')}</Button>
        </div>
      )}
    </div>
  );
}
